import type { ChatMessage } from '../types/chat'
import { isFrenchLanguage } from './chatUiLocale'

/** Mail send Yes/No is rendered on the preview card in the transcript, not the composer bar. */
export function assistantMessageHasInlineMailConfirm(m: ChatMessage): boolean {
  if (m.role !== 'assistant') return false
  if (m.historySnapshot) return false
  if (m.sportsNocYesNoResolved) return false
  const awaiting =
    m.awaitingMailConfirmation === true || m.mailDocument?.awaitingMailConfirmation === true
  if (!awaiting) return false
  const st = String(m.mailDocument?.mail_sent_status || '').trim().toLowerCase()
  if (
    st === 'success' ||
    st === 'sent' ||
    st === 'failure' ||
    st === 'cancelled' ||
    st === 'invalidated'
  ) {
    return false
  }
  return true
}

function hasUserMessageAfter(messages: readonly ChatMessage[], assistantIndex: number): boolean {
  for (let j = assistantIndex + 1; j < messages.length; j++) {
    const row = messages[j]
    if (row.historySnapshot) continue
    if (row.role === 'user') return true
  }
  return false
}

/** True when the assistant is waiting for the document preview before mail confirmation. */
export function messageAwaitingMailDocumentOpen(m: ChatMessage): boolean {
  if (m.role !== 'assistant') return false
  if (m.historySnapshot) return false
  return m.mailDocument?.awaitingDocumentOpen === true
}

/**
 * Newest mail open-then-mail turn still waiting for the ordonnance/PDF to appear.
 * Only the latest ``mailDocument`` assistant row counts (older "opening…" rows may still carry the flag).
 */
export function hasPendingMailDocumentOpen(messages: readonly ChatMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.historySnapshot) continue
    if (m.role === 'user') return false
    if (m.role !== 'assistant') continue
    if (m.mailDocument != null) {
      return messageAwaitingMailDocumentOpen(m)
    }
  }
  return false
}

/** Active open-then-mail workflow (waiting for document and/or mail send preview). */
export function mailOpenThenMailFlowActive(messages: readonly ChatMessage[]): boolean {
  if (hasPendingMailDocumentOpen(messages)) return true
  if (findPendingMailSendConfirm(messages)) return true
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.historySnapshot) continue
    if (m.role === 'user') return false
    if (m.role === 'assistant' && m.mailDocument != null) {
      return messageAwaitingMailDocumentOpen(m) || assistantMessageHasInlineMailConfirm(m)
    }
  }
  return false
}

/** Newest assistant row still waiting for mail yes/no on the in-chat preview card. */
/** Matches backend ``utterance_has_mail_keyword`` (mail / email / courriel). */
export function utteranceHasMailOrEmailKeyword(text: string): boolean {
  return /\b(?:mail|e-?mail|courriel)\b/i.test((text || '').trim())
}

export function findPendingMailSendConfirm(messages: readonly ChatMessage[]): ChatMessage | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.historySnapshot) continue
    if (m.role === 'user') return null
    if (m.role === 'assistant' && assistantMessageHasInlineMailConfirm(m)) {
      if (hasUserMessageAfter(messages, i)) return null
      return m
    }
  }
  return null
}

export function parseMailSendConfirmAnswer(text: string): 'yes' | 'no' | null {
  const t = (text || '').trim().toLowerCase()
  if (!t) return null
  if (
    /^(no|non)\b/.test(t) ||
    /\b(cancel\s+mail|ne\s+pas\s+envoyer|do\s+not\s+send)\b/.test(t) ||
    /\bannuler\s+le\s+courriel\b/.test(t)
  ) {
    return 'no'
  }
  if (
    /^(yes|oui)\b/.test(t) ||
    /\b(send\s+the\s+mail|yes\s+send|envoyer\s+le\s+courriel|oui\s+envoyer)\b/.test(t)
  ) {
    return 'yes'
  }
  return null
}

export function shouldUseHostedChatForMailConfirm(
  text: string,
  messages: readonly ChatMessage[],
): boolean {
  if (!findPendingMailSendConfirm(messages)) return false
  return parseMailSendConfirmAnswer(text) !== null
}

/** Hide Yes/No on mail preview rows immediately when the user declines (before server round-trip). */
export function clearPendingMailSendConfirmInMessages(messages: ChatMessage[]): ChatMessage[] {
  let changed = false
  const next = messages.map((m) => {
    if (!assistantMessageHasInlineMailConfirm(m)) return m
    changed = true
    const md = m.mailDocument
    return {
      ...m,
      awaitingMailConfirmation: false,
      mailDocument: md
        ? {
            ...md,
            awaitingMailConfirmation: false,
          }
        : md,
    }
  })
  return changed ? next : messages
}

export function mailSendConfirmQuestion(fr?: boolean): string {
  const isFr =
    fr ?? isFrenchLanguage()
  return isFr ? 'Voulez-vous envoyer ce courriel ?' : 'Do you want to send this email?'
}

/**
 * Skip auto-opening the PDF iframe when open-then-mail already has the document on screen
 * and the mail preview (or open-wait step) is active — avoids a second open that looks like a refresh.
 */
export function shouldSkipDeferredDocumentAutoOpen(
  messages: readonly ChatMessage[],
  currentPreviewUrl: string | null | undefined,
  pickUrl: string | null | undefined,
): boolean {
  const cur = (currentPreviewUrl || '').trim()
  if (!cur) return false
  if (findPendingMailSendConfirm(messages)) return true
  const pick = (pickUrl || '').trim()
  if (!pick) return mailOpenThenMailFlowActive(messages)
  if (cur !== pick) return false
  return mailOpenThenMailFlowActive(messages)
}

/** Stable iframe key for open-then-mail so later assistant rows do not remount the PDF. */
export function mailDocumentPreviewFrameKey(url: string): string {
  return `mail-open:${url}`
}

/** Stable identity for the in-chat mail preview card (Yes/No must not remount on supersede-only merges). */
export function mailPreviewAnchorFingerprint(m: ChatMessage): string {
  const md = m.mailDocument
  return JSON.stringify({
    id: m.id,
    content: (m.content || '').trim(),
    subject: (md?.mailSubject || '').trim(),
    body: (md?.mailBody || '').trim(),
    recipient: (md?.recipientName || '').trim(),
    recipientType: (md?.recipientType || '').trim(),
    documentType: (md?.documentType || '').trim(),
    awaiting: md?.awaitingMailConfirmation === true || m.awaitingMailConfirmation === true,
  })
}

export function pendingMailPreviewAnchor(
  messages: readonly ChatMessage[],
): { messageId: string; fingerprint: string } | null {
  const m = findPendingMailSendConfirm(messages)
  if (!m) return null
  return { messageId: m.id, fingerprint: mailPreviewAnchorFingerprint(m) }
}

export function mailPreviewAnchorsEqual(
  a: { messageId: string; fingerprint: string } | null | undefined,
  b: { messageId: string; fingerprint: string } | null | undefined,
): boolean {
  if (!a || !b) return false
  return a.messageId === b.messageId && a.fingerprint === b.fingerprint
}

export function mailSendConfirmLabels(fr?: boolean): { yes: string; no: string } {
  const isFr =
    fr ?? isFrenchLanguage()
  return isFr
    ? { yes: 'Oui, envoyer', no: 'Non, annuler' }
    : { yes: 'Yes, send', no: 'No, cancel' }
}
